import React, { useState } from 'react';
import './MonthlyTasksGrid.css';

export default function MonthlyTasksGrid({ tasksData, listColors }) {
  const [hoveredDay, setHoveredDay] = useState(null);
  
  // Get current date info in Pacific Time (to match backend)
  // Use Intl.DateTimeFormat to reliably get Pacific time components
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  });
  const parts = formatter.formatToParts(now);
  const currentYear = parseInt(parts.find(p => p.type === 'year').value);
  const currentMonth = parseInt(parts.find(p => p.type === 'month').value);
  const currentDay = parseInt(parts.find(p => p.type === 'day').value);
  
  const monthName = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'long'
  }).format(now);
  
  // Organize data by date
  const dataByDate = {};
  tasksData.forEach(item => {
    if (!item.date) return;
    if (!dataByDate[item.date]) {
      dataByDate[item.date] = [];
    }
    dataByDate[item.date].push(item);
  });
  
  // Create month grid
  const monthDays = [];
  // Get days in month - this works the same in any timezone
  const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
  
  // Get first day of month in Pacific time (0 = Sunday)
  // Use UTC date then interpret in Pacific timezone
  const firstOfMonthUTC = new Date(Date.UTC(currentYear, currentMonth - 1, 1));
  const firstDayFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'short'
  });
  const firstDayName = firstDayFormatter.format(firstOfMonthUTC);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const firstDayOfMonth = dayNames.indexOf(firstDayName);
  
  // Add empty cells for days before the month starts
  for (let i = 0; i < firstDayOfMonth; i++) {
    monthDays.push({ isEmpty: true, key: `empty-${i}` });
  }
  
  // Add actual days
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayData = dataByDate[dateStr] || [];
    const isFuture = day > currentDay;
    
    // Calculate total tasks and proportions
    const totalTasks = dayData.reduce((sum, item) => sum + item.task_count, 0);
    const segments = dayData.map(item => ({
      listName: item.list_name || item.list_id || 'Unknown',
      taskCount: item.task_count,
      proportion: totalTasks > 0 ? (item.task_count / totalTasks) * 100 : 0,
      color: listColors[item.list_name || item.list_id] || '#94a3b8'
    }));
    
    monthDays.push({
      day,
      dateStr,
      segments,
      totalTasks,
      isFuture,
      isEmpty: false,
      key: dateStr
    });
  }
  
  return (
    <div className="monthly-tasks-grid-container">
      <div className="monthly-tasks-grid-header">
        <h4 className="monthly-tasks-grid-title">{monthName} {currentYear} (Pacific Time)</h4>
        <p className="monthly-tasks-grid-subtitle">Tasks completed per list each day</p>
      </div>
      
      {/* Weekday headers */}
      <div className="monthly-tasks-grid-weekdays">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="monthly-tasks-grid-weekday">{day}</div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="monthly-tasks-grid">
        {monthDays.map(({ isEmpty, day, dateStr, segments, totalTasks, isFuture, key }) => {
          if (isEmpty) {
            return <div key={key} className="monthly-tasks-grid-day empty" />;
          }
          
          const isHovered = hoveredDay === dateStr;
          
          return (
            <div
              key={key}
              className={`monthly-tasks-grid-day ${isFuture ? 'future' : ''} ${totalTasks > 0 ? 'has-data' : ''}`}
              onMouseEnter={() => setHoveredDay(dateStr)}
              onMouseLeave={() => setHoveredDay(null)}
            >
              {totalTasks > 0 && (
                <div className="monthly-tasks-grid-day-segments">
                  {segments.map((segment, idx) => (
                    <div
                      key={idx}
                      className="monthly-tasks-grid-segment"
                      style={{
                        height: `${segment.proportion}%`,
                        backgroundColor: segment.color
                      }}
                    />
                  ))}
                </div>
              )}
              <div className="monthly-tasks-grid-day-number">{day}</div>
              
              {/* Tooltip */}
              {isHovered && totalTasks > 0 && (
                <div className="monthly-tasks-grid-tooltip">
                  <div className="monthly-tasks-grid-tooltip-date">
                    {new Date(dateStr).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                  <div className="monthly-tasks-grid-tooltip-total">
                    Total: {totalTasks} {totalTasks === 1 ? 'task' : 'tasks'}
                  </div>
                  <div className="monthly-tasks-grid-tooltip-lists">
                    {segments.map((segment, idx) => (
                      <div key={idx} className="monthly-tasks-grid-tooltip-item">
                        <div
                          className="monthly-tasks-grid-tooltip-color"
                          style={{ backgroundColor: segment.color }}
                        />
                        <span className="monthly-tasks-grid-tooltip-list">{segment.listName}</span>
                        <span className="monthly-tasks-grid-tooltip-count">{segment.taskCount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

